import { oc } from "@orpc/contract";
import { z } from "zod";
import { OptionalId, ProductSlug, SeoDescription, SeoTitle } from "./product";

/**
 * Groups (`category`, CONTEXT.md) in the admin: how the shop divides the assortment (wegwerp,
 * wasbaar, navulling, accessoires). Richard makes, edits, orders and deletes them; products are put
 * in groups in the product editor (`product.setCategories`). Every write answers the whole list, in
 * order, which the page shows. Errors come back as `{ _tag, message }` with an HTTP status. A group
 * with a holder (`holderId`) is a wisserpagina in the shop; without, a category page with filters.
 */

/** A group as the admin shows it. */
export const Category = z.object({
	id: z.string(),
	name: z.string(),
	/** Its page in the shop: `/producten/{slug}`. */
	slug: z.string(),
	/** 0 is first. */
	position: z.number().int(),
	/** The short text on the group's page. */
	description: z.string(),
	/** `null`: the shop uses the name. */
	seoTitle: z.string().nullable(),
	/** `null`: the shop uses the start of the description. */
	seoDescription: z.string().nullable(),
	/** The holder whose wisserpagina this group is; `null`: none. */
	holderId: z.string().nullable(),
	/** How many products are in the group. */
	productCount: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type Category = z.infer<typeof Category>;

/** Every field of a group, as the form sends it. A new group goes to the end of the list. */
export const CategoryFields = z.object({
	name: z.string().trim().min(1).max(100),
	slug: ProductSlug,
	description: z.string().trim().max(2000).default(""),
	/** `null` or empty: none. */
	seoTitle: SeoTitle.default(null),
	/** `null` or empty: none. */
	seoDescription: SeoDescription.default(null),
	/** The holder whose wisserpagina this group is; `null`, empty or left out: none. */
	holderId: OptionalId,
});
export type CategoryFields = z.input<typeof CategoryFields>;

/** The new order: the id of every group, each once, first to last. */
export const CategoryOrder = z.object({
	ids: z
		.array(z.string())
		.max(1000)
		.refine((ids) => new Set(ids).size === ids.length, "each group once"),
});

const ById = z.object({ categoryId: z.string().min(1) });

export const CategoryContract = {
	list: oc.route({ method: "GET", path: "/admin/categories" }).output(z.array(Category)),
	/** Errors: `CategorySlugTaken` (409), `CategoryHolderNotFound` (422). */
	create: oc
		.route({ method: "POST", path: "/admin/categories" })
		.input(CategoryFields)
		.output(z.array(Category)),
	/** Errors: `CategoryNotFound` (404), `CategorySlugTaken` (409), `CategoryHolderNotFound` (422). */
	update: oc
		.route({ method: "PUT", path: "/admin/categories/{categoryId}" })
		.input(CategoryFields.extend(ById.shape))
		.output(z.array(Category)),
	/**
	 * Deletes the group, takes its products out of it and its questions off it (they stay on the FAQ
	 * page), in one batch. Errors: `CategoryNotFound` (404).
	 */
	delete: oc
		.route({ method: "DELETE", path: "/admin/categories/{categoryId}" })
		.input(ById)
		.output(z.array(Category)),
	/** Errors: `CategoryOrderOutdated` (409): the order does not name every group exactly once. */
	reorder: oc
		.route({ method: "PUT", path: "/admin/categories/order" })
		.input(CategoryOrder)
		.output(z.array(Category)),
};
